<ng-container *ngIf="{
  updateConfiguration: updateConfiguration$ | async,
  loggedUser: loggedUser$ | async,
  showSaveButton: showSaveButton$ | async,
  showMeterInversion: showMeterInversion$ | async,
} as resolved">
  <ng-container *ngIf="resolved.updateConfiguration as config">
    <section
      id="plant-configuration"
      class="custom-block">
      <section
        *ngIf="config.isConnextOffline"
        class="border-1 fx-center fx-gap-3 p-1 radius-4 radius-2 border-grey-100">
        <mat-icon class="text-orange-400">
          info
        </mat-icon>
        <customer-title-content
          class="fx-g-1"
          [content]="labels.connextDisconnectedDisclaimer">
        </customer-title-content>
      </section>
      <customer-configuration-section
        [labels]="{
          contractPowerDescription: labels.contractPowerDescription,
          contractPowerPlaceholder: labels.contractPowerPlaceholder,
          contractPowerTitle: labels.contractPowerTitle,
          externalPower: labels.externalPower,
          externalPowerConsider: labels.externalPowerConsider,
          externalPowerDescription: labels.externalPowerDescription,
          externalPowerNotConsider: labels.externalPowerNotConsider,
          externalPowerSelectionPlaceholder: labels.externalPowerSelectionPlaceholder,
          locationDescription: labels.locationDescription,
          locationTitle: labels.locationTitle,
          mapCoordinationPlaceholder: labels.mapCoordinationPlaceholder,
          meterSignDescription: labels.meterSignDescription,
          meterSignInverted: labels.meterSignInverted,
          meterSignNormal: labels.meterSignNormal,
          meterSignPlaceholder: labels.meterSignPlaceholder,
          meterSignTitle: labels.meterSignTitle,
          phaseSelectPlaceholder: labels.phaseSelectPlaceholder,
          plantCreationConnextApply: labels.plantCreationConnextApply,
          plantCreationConnextDisclaimer: labels.plantCreationConnextDisclaimer,
          plantCreationTitle: labels.plantCreationTitle,
          plantName: labels.plantName,
          plantNamePlaceholder: labels.plantNamePlaceholder,
          plantTypeDescription: labels.plantTypeDescription,
          plantTypeDisabled: labels.plantTypeDisabled,
          plantTypeTitle: labels.plantTypeTitle,
          sensorSignDescription: labels.sensorSignDescription,
          sensorSignTitle: labels.sensorSignTitle,
          singlePhase: labels.singlePhase,
          threePhase: labels.threePhase,
          monitoringModeTitle: labels.monitoringModeTitle,
          monitoringModeDescription: labels.monitoringModeDescription,
          monitoringModePlaceholder: labels.monitoringModePlaceholder,
          monitoringModeInverterMaster: labels.monitoringModeInverterMaster,
          monitoringModeMeterMaster: labels.monitoringModeMeterMaster,
          monitoringModeInternalSensor: labels.monitoringModeInternalSensor,
          monitoringModePowerMagic: labels.monitoringModePowerMagic,
          monitoringModeNone: labels.monitoringModeNone,
          error: labels.error,
          unauthorized: labels.unauthorized,
          deviceDisconnected: labels.deviceDisconnected,
          unknown: labels.unknown,
          timeout: labels.timeout,
        }"
        [system]="config.system"
        [isConnextLoggerSelected]="config.isConnextLoggerSelected"
        [connextConfiguration]="config.connextConfiguration"
        [isConnextLoggerOwner]="config.isSystemOwner || config.isSystemAdmin || config.isSuperUser"
        [connextSerial]="config.connextSerial"
        [isAzzurroHubLoggerSelected]="config.isAzzurroHubLoggerSelected"
        [azzurroHubConfiguration]="config.azzurroHubConfiguration"
        [isAzzurroHubLoggerOwner]="config.isSystemOwner || config.isSystemAdmin || config.isSuperUser"
        [azzurroHubSerial]="config.azzurroHubSerial"
        [isAzzurroHubOffline]="config.isAzzurroHubOffline"
        [virtualConfiguration]="virtualConfiguration"
        [connextMeterConfig]="config.connextMeterConfig"
        [connextSensorConfig]="config.connextSensorConfig"
        [connextExternalConfig]="config.connextExternalConfig"
        [isConnextOffline]="config.isConnextOffline"
        [showMeterInversionInput]="resolved.showMeterInversion"
        [needConnextDefaultConfiguration]="false"
        [isVerifiedMode]="true"
        (applyConnextChanges)="updateSystemSettings(config)"
        (applyAzzurroHubChanges)="updateSystemSettings(config)">
      </customer-configuration-section>
      <!-- Save button for name/location changes (virtual, offline, or AzzurroHub/Connext systems) -->
      <button
        *ngIf="resolved.showSaveButton"
        class="mt-3 custom-mat-button-primary"
        mat-button
        data-testid="save-plant-settings"
        [disabled]="config.isAzzurroHubLoggerSelected
          ? (azzurroHubConfiguration.controls.plantName.invalid || azzurroHubConfiguration.controls.coordinates.invalid)
          : config.isConnextLoggerSelected
            ? (connextConfiguration.controls.plantName.invalid || connextConfiguration.controls.coordinates.invalid)
            : virtualConfiguration.invalid"
        (click)="updateSystemSettings(config)">
        {{ 'i18n.Common.save' | translate }}
      </button>
    </section>
    <section
      *ngIf="config.isSystemOwner || config.isSuperUser"
      id="plant-users"
      class="custom-block">
      <customer-users-section
        [labels]="labels"
        [connextSerial]="config.connextSerial"
        [connextUsers]="config.connextUsers"
        [isVerifiedMode]="true"
        [useDirectDelete]="true"
        (onUserChanges)="onUserAdded($event, config)"
        (deleteUsers)="deleteSelectedUsers($event, config, resolved.loggedUser!)">
        <button
          menu-options
          class="custom-mat-button-primary ml-0"
          mat-button
          data-testid="save-permissions"
          [disabled]="!(canSaveUsers$ | async)"
          (click)="updateUsersInPlant(config, resolved.loggedUser!)">
          {{ 'i18n.Common.save' | translate }}
        </button>
      </customer-users-section>
    </section>
    <section
      *ngIf="(config.isSystemOwner || config.isSuperUser) && !(config?.system?.configuration?.connextId)"
      id="notification-configuration"
      class="custom-block fx-row">
      <lib-notification-configuration
        class="w-full"
        [labels]="labels"
        [system]="config.system">
      </lib-notification-configuration>
    </section>
    <section
      id="plant-remove"
      class="custom-block fx-row fx-gap-2 fx-align-center fx-justify-between ">
      <customer-title-content
        class="fx-g-1 fx-row fx-b-0"
        [title]="labels.deletePlantTitle"
        [content]="labels.deletePlantContent">
      </customer-title-content>
      <div
        *ngIf="resolved.loggedUser"
        class="fx-row fx-wrap fx-gap-4">
        <button
          *ngIf="showRemoveMeButton$ | async"
          data-testid="delete-plant-for-me-button"
          class="custom-mat-button-warning min-w-20"
          mat-button
          (click)="removeMeToSystem(config, resolved.loggedUser)">
          {{ labels.deleteForMe }}
        </button>
        <button
          *ngIf="config.isSystemOwner || config.isSuperUser"
          data-testid="delete-plant-for-all-button"
          class="custom-mat-button-warning min-w-20"
          mat-button
          (click)="deleteSystem(config, resolved.loggedUser)">
          {{ labels.deleteForAll }}
        </button>
      </div>
    </section>
  </ng-container>
</ng-container>
